import { getRefreshToken } from "../../lib/storeManager";
import { throwError } from "../../lib/throwError";
import { post } from "../../services/axiosUitils";
import { AuthRes, SocialLoginRes } from "../../utils/types/auth";

type Credentials = { email: string; password: string };
type SignupCredentials = {
  email?: string;
  password?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
};

type SignupOrganizationCredentials = {
  work_email: string;
  password: string;
  full_name: string;
  organization: string;
};

class AuthService {
  login = async (
    credentials: Credentials,
    notifyMessage?: boolean
  ): Promise<AuthRes> => {
    if (!credentials) {
      throwError("Credentials are required", "AuthService");
    }
    return await post<AuthRes>("/auth/login/", credentials, { notifyMessage });
  };

  signup = async (
    credentials: SignupCredentials,
    notifyMessage?: boolean
  ): Promise<AuthRes> => {
    if (!credentials) {
      throwError("Credentials are required", "AuthService");
    }
    return await post<AuthRes>("/auth/", credentials, { notifyMessage });
  };

  endUserSignup = async (
    credentials: SignupCredentials,
    notifyMessage?: boolean
  ): Promise<AuthRes> => {
    if (!credentials) {
      throwError("Credentials are required", "AuthService");
    }
    return await post<AuthRes>("/city_collab/end-user-sign-up/", credentials, {
      notifyMessage,
    });
  };

  signupOrganization = async (
    credentials: SignupOrganizationCredentials,
    notifyMessage?: boolean
  ): Promise<AuthRes> => {
    if (!credentials) {
      throwError("Credentials are required", "AuthService");
    }
    return await post<AuthRes>(
      "/city_collab/organization-sign-up/",
      credentials,
      { notifyMessage }
    );
  };

  logout = async (notifyMessage?: boolean): Promise<void> => {
    const refresh = getRefreshToken();
    if (!refresh) {
      throwError("Refresh token is required", "AuthService");
    }
    await post("/auth/logout/", { refresh }, { notifyMessage });
  };

  socialLogin = async (
    provider: string,
    notifyMessage?: boolean
  ): Promise<SocialLoginRes> => {
    return await post<SocialLoginRes>(`/auth/${provider}/login/`, {
      notifyMessage,
    });
  };

  forgotPassword = async (
    email: string,
    notifyMessage?: boolean
  ): Promise<any> => {
    return await post<any>(
      `/forgot-password-request/`,
      { email },
      { notifyMessage }
    );
  };
  forgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    },
    notifyMessage?: boolean
  ): Promise<any> => {
    return await post<any>(
      `/forgot-password-reset/${id}/`,
      {
        new_password: payload.new_password,
        confirm_password: payload.confirm_password,
      },
      { notifyMessage }
    );
  };

  refreshToken = async (notifyMessage?: boolean): Promise<AuthRes> => {
    const refresh = getRefreshToken()!;
    if (!refresh) {
      throwError("Refresh token is required", "AuthService");
    }
    return await post<AuthRes>(
      "/auth/token-refresh/",
      new URLSearchParams({ refresh }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        notifyMessage,
      }
    );
  };

  resetPassword = async (
    values: {
      current_password: string;
      new_password: string;
    },
    notifyMessage?: boolean
  ): Promise<any> => {
    return await post<any>(`/auth/password/change/`, values, { notifyMessage });
  };
}

const instance = new AuthService();
export default instance;
